import { Button, Label, Modal, Select, Textarea, TextInput, ToggleSwitch } from "flowbite-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createBrand, updateBrand } from "../../api/brands";
import LogoUploader from "../uploaders/LogoUploader";
import { Tooltip } from "react-tooltip";
import { FaInfoCircle } from "react-icons/fa";
import ReactSelect from "react-select";

const CreateModal = ({ openModal, setOpenModal, fetchBrands, brandToEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    website: '',
    services: '',
    mention: false,
    tone: '',
    core: [],
    toneOther: '',
    coreOther: '',
  });

  const tones = ['Professional & Corporate', 'Friendly & Approachable', 'Innovative & Disruptive', 'Luxury & Exclusive', 'Quirky & Fun', 'Other'];
  const cores = ['Sustainability', 'Customer-Centric', 'Innovation & Technology', 'Quality & Excellence', 'Trust & Transparency', 'Creativity & Design', 'Speed & Efficiency', 'Affordable & Accessible', 'Other'];

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData({
      name: brandToEdit?.name || '',
      bio: brandToEdit?.bio || '',
      website: brandToEdit?.website || '',
      services: brandToEdit?.services || '',
      mention: brandToEdit?.mention && brandToEdit.mention === 'true' ? true : false,
      tone: brandToEdit?.tone || '',
      core: brandToEdit?.core || [],
      toneOther: brandToEdit?.toneOther || '',
      coreOther: brandToEdit?.coreOther || '',
    });
  }, [brandToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const data = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'logo') {
          const fileInput = document.querySelector('input[name="logo"]');

          if (fileInput && fileInput.files.length) {
            data.append('logo', fileInput.files[0]);
          }
        } else if (Array.isArray(value)) {
          value.forEach(item => data.append(`${key}[]`, item));
        } else {
          data.append(key, value);
        }
      });

      brandToEdit ? await updateBrand({
        data,
        id: brandToEdit._id
      }) : await createBrand(data);

      toast.success(`${formData.name} client has been ${brandToEdit ? 'updated' : 'created'} successfully`);
      fetchBrands();
      setOpenModal(false);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || `Failed to ${brandToEdit ? 'update' : 'create'} client`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      show={openModal}
      size="md"
      onClose={() => setOpenModal(false)}
      popup
      theme={{
        body: {
          base: 'flex-1 p-6',
        }
      }}
    >
      <Modal.Header />
      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-6">
          <h3 className="text-xl font-medium text-gray-900">{brandToEdit ? 'Edit' : 'Add'} client</h3>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="name" value="Client Name" />
            </div>
            <TextInput
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="bio" value="Bio" />
            </div>
            <Textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="logo" value="Logo" />
            </div>
            <LogoUploader
              name="logo"
              value={formData.logo || ''}
              onChange={handleChange}
              existingLogo={brandToEdit?.logo}
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="website" value="Website" />
            </div>
            <TextInput
              id="website"
              name="website"
              value={formData.website}
              type="url"
              onChange={handleChange}
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="services" value="Services" />
            </div>
            <Textarea
              id="services"
              name="services"
              value={formData.services}
              helperText="List services offered (one per line)"
              onChange={handleChange}
            />
          </div>

          <div>
            <div className="flex gap-2 mb-2">
              <Label htmlFor="mention" value="Mention Client’s Name" />
              <button data-tooltip-id="mention" data-tooltip-content="If enabled, the AI will introduce the company explicitly in initial outreach.">
                <FaInfoCircle />
              </button>
              <Tooltip
                id="mention"
                style={{
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              />
            </div>
            <ToggleSwitch
              checked={formData.mention}
              onChange={() => {
                handleChange({
                  target: {
                    name: 'mention',
                    value: !formData.mention,
                  }
                });
              }}
              theme={{
                toggle: {
                  base: 'after:rounded-full rounded-full border group-focus:ring-4 group-focus:ring-cyan-500/25',
                  checked: {
                    on: 'after:bg-white after:translate-x-full',
                    off: 'after:bg-gray-400 dark:after:bg-gray-500 border-gray-200 bg-gray-200 dark:border-gray-600 dark:bg-gray-700',
                  },
                },
              }}
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="tone" value="Company Tone & Communication Style" />
            </div>
            <Select
              id="tone"
              onChange={handleChange}
              name="tone"
              value={formData.tone || ''}
            >
              <option
                value=""
                disabled
              >
                Select
              </option>
              {tones.map(tone => (
                <option
                  value={tone}
                  key={tone}
                >
                  {tone}
                </option>
              ))}
            </Select>

            {formData.tone === 'Other' && (
              <div className="mt-2">
                <TextInput
                  id="toneOther"
                  name="toneOther"
                  value={formData.toneOther}
                  onChange={handleChange}
                  placeholder="Please describe"
                />
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="core" value="Core Values" />
            </div>
            <ReactSelect
              isMulti
              onChange={value => {
                handleChange({
                  target: {
                    name: 'core',
                    value: value.map(v => v.value),
                  },
                })
              }}
              className="text-xs"
              options={cores.map(core => ({
                value: core,
                label: core,
              }))}
              defaultValue={brandToEdit ? cores.filter(core => brandToEdit.core.includes(core)).map(core => ({
                value: core,
                label: core,
              })) : null}
            />

            {formData.core.includes('Other') && (
              <div className="mt-2">
                <TextInput
                  id="coreOther"
                  name="coreOther"
                  value={formData.coreOther}
                  onChange={handleChange}
                  placeholder="Please describe"
                />
              </div>
            )}
          </div>

          <div className="w-full">
            <Button
              type="submit"
              isProcessing={loading}
              disabled={loading}
            >
              {brandToEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default CreateModal;
